import { Component } from '@angular/core';
import { HeaderComponent } from '@components/dashboard/header/header.component';
import { NavComponent } from '@components/dashboard/navigation/nav.component';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, ActivatedRoute } from '@angular/router';
import { MomentModule } from 'ngx-moment';
import { Apollo } from 'apollo-angular';
import { GoogleMapsModule, GoogleMap } from '@angular/google-maps';
import { AuthStore } from '@store/auth.store';
import { Transactions } from '@interfaces/models.interface';
import {
  BOOKING_EXPRESS_TRANSACTIONS,
  BOOKING_RIDE_MOTOTAXI_TRANSACTIONS,
  FLEET_BOOKING_TRANSACTION_DETAILS,
} from '@gql_queries/transactions/queries';

@Component({
  selector: 'app-dashboard-transaction-details',
  standalone: true,
  imports: [HeaderComponent, NavComponent, CommonModule, RouterModule, MomentModule, GoogleMapsModule, GoogleMap],
  templateUrl: './dashboard-transaction-details.component.html',
  styleUrl: './dashboard-transaction-details.component.css'
})
export class DashboardTransactionDetailsComponent {
  serviceType: string = '';
  transId: string = '';
  detailViewed: Record<string, any> = {};
  notFound: boolean = false;
  readonly defaultAvatar = '../../../../assets/okito/images/avatar.png';

  transTabs: Record<string, any> = {
    express: false,
    okride: false,
    mototaxi: false,
    oktaxi: false,
  };

  //FOR THE MAP (BOOKING DETAILS)
  polyLinePath: google.maps.LatLng[] = [];
  display: any;
  center: google.maps.LatLngLiteral = {
    lat: 22.2736308,
    lng: 70.7512555
  };
  zoom = 15;
  polyLineOptions: google.maps.PolylineOptions = {
    strokeColor: '#D81960',
    strokeOpacity: 1.0,
    strokeWeight: 5,
    clickable: false,
    editable: false,
    visible: true,
    geodesic: true,
  };

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private apollo: Apollo,
    private authStore: AuthStore,
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      const serviceType = params.get('serviceType');
      const transId = params.get('transId');

      if (!serviceType || !transId) {
        this.router.navigate(['/admin/transactions']);
        return;
      }

      this.serviceType = serviceType;
      this.transId = transId;

      Object.keys(this.transTabs).forEach((x) => { this.transTabs[x] = false; });
      if (this.transTabs[serviceType] === undefined) {
        this.router.navigate(['/admin/transactions']);
        return;
      }
      this.transTabs[serviceType] = true;

      this.loadTransaction();
    });
  }

  private loadTransaction(): void {
    let requestQry;

    if (this.serviceType === 'express') {
      requestQry = this.apollo.watchQuery({
        fetchPolicy: 'network-only',
        query: BOOKING_EXPRESS_TRANSACTIONS,
        variables: { sortBy: { sort: 'dateCreated', direction: 'desc' } },
      });
    } else if (this.serviceType === 'oktaxi') {
      requestQry = this.apollo.watchQuery({
        fetchPolicy: 'network-only',
        query: FLEET_BOOKING_TRANSACTION_DETAILS,
        variables: {
          fleetBookingId: this.transId,
        },
      });
    } else {
      requestQry = this.apollo.watchQuery({
        fetchPolicy: 'network-only',
        query: BOOKING_RIDE_MOTOTAXI_TRANSACTIONS,
        variables: {
          service: this.serviceType,
          sortBy: { sort: 'dateCreated', direction: 'desc' },
        },
      });
    }

    requestQry.valueChanges.subscribe({
      next: (res: any) => {
        if (res.loading) {
          return;
        }

        let match: Transactions | undefined;

        if (this.serviceType === 'oktaxi') {
          const details = res?.data?.fleetBookingTransactionDetails;
          match = details ? this.normalizeFleetBooking(details) : undefined;
        } else if (this.serviceType === 'express') {
          const items: Transactions[] = res?.data?.bookingExpressTxns || [];
          match = items.find((x) => x.bookingExpressId === this.transId);
        } else {
          const items: Transactions[] = res?.data?.bookingRideTxns || [];
          match = items.find((x) => x.bookingRideId === this.transId);
        }

        if (!match) {
          this.notFound = true;
          return;
        }

        this.notFound = false;
        this.detailViewed = match;
      },
      error: (error) => {
        if (error.message === 'Unauthorized' || error?.networkError?.status === 403) {
          this.authStore.logout().subscribe(() => {
            this.router.navigate(['/admin/login']);
          });
          return;
        }
        this.notFound = true;
      },
    });
  }

  /** Align fleet booking payload to ride/express list + details shape. */
  private normalizeFleetBooking(item: any): Transactions {
    return {
      ...item,
      bookingRideId: item.fleetBookingId,
      fleetBookingId: item.fleetBookingId,
      bookingExpressId: item.bookingExpressId || '',
      notes: item.remarks || item.notes || '',
      rating: item.rating || null,
      partnerRating: item.rating || null,
    };
  }

  getDetailTransactionId(): string {
    if (this.transTabs['express']) {
      return this.detailViewed['bookingExpressId'];
    }
    if (this.transTabs['oktaxi']) {
      return this.detailViewed['fleetBookingId'] || this.detailViewed['bookingRideId'];
    }
    return this.detailViewed['bookingRideId'];
  }

  getFleetOperatorName(): string {
    return this.detailViewed['fleet']?.operatorName
      || this.detailViewed['partner']?.fleetRegistry?.fleet?.operatorName
      || '---';
  }

  getPassengerPhoto(): string {
    return this.detailViewed['user']?.photo || this.defaultAvatar;
  }

  getPartnerPhoto(): string {
    return this.detailViewed['partner']?.photo || this.defaultAvatar;
  }

  returnToList(): void {
    this.router.navigate(['/admin/transactions']);
  }

  moveMap(event: google.maps.MapMouseEvent) {
    if (event.latLng != null) this.center = (event.latLng.toJSON());
  }

  move(event: google.maps.MapMouseEvent) {
    if (event.latLng != null) this.display = event.latLng.toJSON();
  }
}
